/**
 * Shared admin UI primitives used by the Index Operations pages.
 *
 * These components implement the global UX patterns from
 * ADMIN_PORTAL_UX_REQUIREMENTS.md:
 *   - Long-running operation pattern (button + spinner + elapsed timer)
 *   - Confirmation dialog (typed phrase to enable primary action)
 *   - "Last run" card persisted in localStorage
 *   - Result rendering helpers (summary cards, samples, raw JSON)
 */
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Loader2, X } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows } from '../../styles/theme';
import type { LastRunRecord } from '../../services/adminApi';

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinIcon = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[5]};
  background: ${colors.primary[600]};
  color: ${colors.white};
  border: none;
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${colors.primary[700]};
  }

  &:disabled {
    background: ${colors.gray[300]};
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[5]};
  background: ${colors.white};
  color: ${colors.text.primary};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: ${colors.background.secondary};
    border-color: ${colors.text.tertiary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DangerButton = styled(PrimaryButton)`
  background: ${colors.error[600]};

  &:hover:not(:disabled) {
    background: ${colors.error[700]};
  }
`;

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export const Card = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[6]};
  box-shadow: ${shadows.sm};
  margin-bottom: ${spacing[6]};
`;

export const CardTitle = styled.h3`
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin: 0 0 ${spacing[2]} 0;
`;

export const CardDescription = styled.p`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
  margin: 0 0 ${spacing[5]} 0;
  line-height: ${typography.lineHeights.relaxed};
`;

// ---------------------------------------------------------------------------
// Stat / metric cards
// ---------------------------------------------------------------------------

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
`;

const StatBox = styled.div<{ tone?: 'success' | 'warning' | 'error' | 'neutral' }>`
  background: ${(p) => {
    switch (p.tone) {
      case 'success': return colors.success[50];
      case 'warning': return colors.warning[50];
      case 'error':   return colors.error[50];
      default:        return colors.background.secondary;
    }
  }};
  border: 1px solid ${(p) => {
    switch (p.tone) {
      case 'success': return colors.success[200];
      case 'warning': return colors.warning[200];
      case 'error':   return colors.error[200];
      default:        return colors.border.light;
    }
  }};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[4]};
`;

const StatLabel = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
  font-weight: ${typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${spacing[2]};
`;

const StatValue = styled.div<{ tone?: 'success' | 'warning' | 'error' | 'neutral' }>`
  font-size: ${typography.fontSizes['3xl']};
  font-weight: ${typography.fontWeights.bold};
  color: ${(p) => {
    switch (p.tone) {
      case 'success': return colors.success[700];
      case 'warning': return colors.warning[700];
      case 'error':   return colors.error[700];
      default:        return colors.text.primary;
    }
  }};
  line-height: 1.1;
`;

const StatHint = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
  margin-top: ${spacing[2]};
`;

interface MetricProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'success' | 'warning' | 'error' | 'neutral';
}

export const Metric: React.FC<MetricProps> = ({ label, value, hint, tone }) => (
  <StatBox tone={tone}>
    <StatLabel>{label}</StatLabel>
    <StatValue tone={tone}>{value}</StatValue>
    {hint && <StatHint>{hint}</StatHint>}
  </StatBox>
);

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

export const Banner = styled.div<{ tone: 'success' | 'warning' | 'error' | 'info' }>`
  padding: ${spacing[4]} ${spacing[5]};
  border-radius: ${borderRadius.lg};
  margin-bottom: ${spacing[5]};
  font-size: ${typography.fontSizes.sm};
  line-height: ${typography.lineHeights.relaxed};
  background: ${(p) => {
    switch (p.tone) {
      case 'success': return colors.success[50];
      case 'warning': return colors.warning[50];
      case 'error':   return colors.error[50];
      default:        return colors.primary[50];
    }
  }};
  color: ${(p) => {
    switch (p.tone) {
      case 'success': return colors.success[800];
      case 'warning': return colors.warning[800];
      case 'error':   return colors.error[800];
      default:        return colors.primary[800];
    }
  }};
  border: 1px solid ${(p) => {
    switch (p.tone) {
      case 'success': return colors.success[200];
      case 'warning': return colors.warning[200];
      case 'error':   return colors.error[200];
      default:        return colors.primary[200];
    }
  }};
`;

// ---------------------------------------------------------------------------
// Long-running progress bar
// ---------------------------------------------------------------------------

const ProgressWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[4]};
  background: ${colors.primary[50]};
  border: 1px solid ${colors.primary[200]};
  border-radius: ${borderRadius.lg};
  margin: ${spacing[4]} 0;
  color: ${colors.primary[800]};
`;

function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

interface RunningIndicatorProps {
  /** Wall-clock time when the run started (Date.now()). */
  startedAt: number;
  message?: string;
}

export const RunningIndicator: React.FC<RunningIndicatorProps> = ({
  startedAt,
  message,
}) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);
  return (
    <ProgressWrap>
      <SpinIcon size={20} />
      <div>
        <div style={{ fontWeight: 600 }}>
          {message ?? 'Running…'} ({fmtElapsed(Date.now() - startedAt)})
        </div>
        <div style={{ fontSize: 12, opacity: 0.85 }}>
          This may take several minutes — leave this tab open.
        </div>
      </div>
    </ProgressWrap>
  );
};

// ---------------------------------------------------------------------------
// Confirmation dialog (typed phrase)
// ---------------------------------------------------------------------------

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing[4]};
`;

const ModalCard = styled.div`
  background: ${colors.white};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[6]};
  max-width: 520px;
  width: 100%;
  box-shadow: ${shadows.lg};
`;

const ModalHeader = styled.div`
  background: ${colors.error[600]};
  color: ${colors.white};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.md};
  font-weight: ${typography.fontWeights.semibold};
  margin: -${spacing[2]} 0 ${spacing[4]};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${spacing[3]};
  justify-content: flex-end;
  margin-top: ${spacing[5]};
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.base};
  font-family: ${typography.fontFamilies?.mono ?? 'monospace'};
  margin-top: ${spacing[3]};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
  }
`;

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  /** What will happen — appears in the dialog body. */
  body: React.ReactNode;
  /** Phrase the user must type to enable the primary button. */
  confirmPhrase: string;
  /** Custom helper above the input. Defaults to 'Type "<phrase>" to confirm:'. */
  promptText?: React.ReactNode;
  primaryLabel: string;
  isProcessing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title = 'Destructive action',
  body,
  confirmPhrase,
  promptText,
  primaryLabel,
  isProcessing,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTyped('');
      // Focus the input shortly after opening.
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isProcessing) onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, isProcessing, onCancel]);

  if (!open) return null;

  const armed = typed.trim() === confirmPhrase;

  return (
    <ModalBackdrop onClick={() => !isProcessing && onCancel()}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <span>{title}</span>
          <X
            size={18}
            style={{ cursor: isProcessing ? 'not-allowed' : 'pointer' }}
            onClick={() => !isProcessing && onCancel()}
          />
        </ModalHeader>
        <div style={{ fontSize: 14, color: colors.text.primary, lineHeight: 1.6 }}>
          {body}
        </div>
        <div style={{ marginTop: spacing[4], fontSize: 13, color: colors.text.secondary }}>
          {promptText ?? (
            <>
              Type <code>{confirmPhrase}</code> to confirm:
            </>
          )}
        </div>
        <ConfirmInput
          ref={inputRef}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={isProcessing}
          autoComplete="off"
          spellCheck={false}
        />
        <ModalActions>
          <SecondaryButton onClick={onCancel} disabled={isProcessing}>
            Cancel
          </SecondaryButton>
          <DangerButton
            onClick={onConfirm}
            disabled={!armed || isProcessing}
          >
            {isProcessing && <SpinIcon size={16} />}
            {primaryLabel}
          </DangerButton>
        </ModalActions>
      </ModalCard>
    </ModalBackdrop>
  );
};

// ---------------------------------------------------------------------------
// Raw JSON viewer (collapsible)
// ---------------------------------------------------------------------------

const Details = styled.details`
  margin-top: ${spacing[4]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  background: ${colors.background.secondary};

  > summary {
    padding: ${spacing[3]} ${spacing[4]};
    cursor: pointer;
    font-size: ${typography.fontSizes.sm};
    font-weight: ${typography.fontWeights.medium};
    color: ${colors.text.secondary};
    list-style: none;
  }

  > summary::-webkit-details-marker {
    display: none;
  }

  pre {
    margin: 0;
    padding: ${spacing[4]};
    border-top: 1px solid ${colors.border.light};
    background: ${colors.background.tertiary};
    font-size: 12px;
    overflow-x: auto;
    max-height: 400px;
    color: ${colors.text.primary};
  }
`;

interface RawJsonProps {
  data: unknown;
  label?: string;
}

export const RawJson: React.FC<RawJsonProps> = ({ data, label = 'Raw response' }) => (
  <Details>
    <summary>▸ {label}</summary>
    <pre>{JSON.stringify(data, null, 2)}</pre>
  </Details>
);

// ---------------------------------------------------------------------------
// Last run card
// ---------------------------------------------------------------------------

const LastRunWrap = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[4]};
  margin-top: ${spacing[5]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};
`;

const LastRunHeader = styled.div`
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
`;

interface LastRunCardProps {
  record: LastRunRecord<unknown> | null;
}

export const LastRunCard: React.FC<LastRunCardProps> = ({ record }) => {
  if (!record) return null;
  const ts = new Date(record.timestamp);
  return (
    <LastRunWrap>
      <LastRunHeader>Last run</LastRunHeader>
      <div>
        {ts.toLocaleString()}
        {record.triggeredBy ? <> · by {record.triggeredBy}</> : null}
        {record.durationMs != null
          ? <> · {(record.durationMs / 1000).toFixed(1)}s</>
          : null}
      </div>
      {record.summary && (
        <div style={{ marginTop: spacing[2] }}>
          {Object.entries(record.summary).map(([k, v]) => (
            <span key={k} style={{ marginRight: spacing[3] }}>
              <strong>{k}:</strong> {String(v)}
            </span>
          ))}
        </div>
      )}
      {record.response !== undefined && <RawJson data={record.response} />}
    </LastRunWrap>
  );
};

// ---------------------------------------------------------------------------
// Simple table for sample rows
// ---------------------------------------------------------------------------

export const SampleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.fontSizes.sm};
  margin-top: ${spacing[3]};

  th, td {
    text-align: left;
    padding: ${spacing[2]} ${spacing[3]};
    border-bottom: 1px solid ${colors.border.light};
  }

  th {
    font-weight: ${typography.fontWeights.semibold};
    color: ${colors.text.secondary};
    background: ${colors.background.secondary};
  }

  td {
    color: ${colors.text.primary};
  }
`;

// ---------------------------------------------------------------------------
// Form primitives
// ---------------------------------------------------------------------------

export const Label = styled.label`
  display: block;
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[2]};
`;

export const Input = styled.input`
  width: 100%;
  padding: ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.sm};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
  }

  &:disabled {
    background: ${colors.background.secondary};
    color: ${colors.text.tertiary};
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.sm};
  background: ${colors.white};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
  }
`;

export const Field = styled.div`
  margin-bottom: ${spacing[4]};
`;

export const InlineLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
  cursor: pointer;
  margin-right: ${spacing[5]};
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: ${spacing[3]};
  align-items: center;
  flex-wrap: wrap;
  margin-top: ${spacing[4]};
`;
